import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import styles from "../../styles/components/common/Modal.module.css";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getFocusableElements = (container) => {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true",
  );
};

const Modal = ({
  children,
  onClose,
  className = "",
  contentClassName = "",
  isOpen = true,
  ariaLabelledBy,
  ariaLabel,
  hideDefaultClose = false,
  fullScreenOnMobile = false,
}) => {
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  // Keep onClose in a ref so parent inline handlers don't re-run this effect
  // (which would steal focus from inputs on every keystroke).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Lock body scroll, manage focus (trap + restore), and handle Escape/Tab
  // while the modal is open. This keeps keyboard users inside the dialog
  // and returns focus to the trigger element once it closes.
  // Depend only on isOpen — not onClose — so typing in form fields inside
  // the modal does not remount listeners or re-focus the close button.
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    previouslyFocusedRef.current = document.activeElement;

    const dialog = dialogRef.current;
    const focusable = getFocusableElements(dialog);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (dialog) {
      dialog.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onCloseRef.current?.();
        return;
      }

      if (e.key !== "Tab") return;

      const currentDialog = dialogRef.current;
      const focusableEls = getFocusableElements(currentDialog);
      if (focusableEls.length === 0) {
        e.preventDefault();
        currentDialog?.focus();
        return;
      }

      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);

      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleContentWheel = (e) => {
    // Prevent all wheel events from bubbling to prevent background scrolling
    e.stopPropagation();
  };

  return createPortal(
    <div
      className={`${styles.modalOverlay} ${
        fullScreenOnMobile ? styles.fullScreenOnMobileOverlay : ""
      }`.trim()}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.modalBackdrop}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-label={!ariaLabelledBy ? ariaLabel : undefined}
        tabIndex={-1}
        className={`${styles.modalBox} ${
          fullScreenOnMobile ? styles.fullScreenOnMobile : ""
        } ${className}`.trim()}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!hideDefaultClose && (
          <>
            {/* Close button container */}
            <div className={styles.modalHeader}>
              <button
                type="button"
                className={styles.modalClose}
                onClick={onClose}
                aria-label="Close dialog"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            {/* Separator line */}
            <div className={styles.modalSeparator}></div>
          </>
        )}

        {/* Content */}
        <div
          className={`${styles.modalContent} ${contentClassName}`.trim()}
          onWheel={handleContentWheel}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default React.memo(Modal);
